import {
  CertificateIcon,
  CheckCircleIcon,
  DateRangeIcon,
  HomeIcon,
  InfoIcon,
  LibraryBooksIcon,
  PlayCircleIcon,
  WarningIcon,
} from '../icons';
import { Box, Card, Typography } from '../ui';
import { CourseCEInfo } from '../../utils/courseCEInfo';
import defaultPaceBadge from '../../assets/images/agd-pace-badge.png';
import EmptyTabMessage from './EmptyTabMessage';

interface CourseCETabProps {
  /** Normalized CE info (AGD boilerplate already substituted). */
  ceInfo: CourseCEInfo;
  /** CE credit hours awarded, if set on the course. Shown as the
   *  lead stat tile. */
  ceHours?: number;
  /** Whether the admin authored any CE text. When false the tab shows
   *  its empty state instead of the accreditation dashboard. */
  hasContent: boolean;
}

/**
 * CourseCETab
 *
 * The "CE Information" tab on the learner course-detail page:
 * PACE seal hero, at-a-glance stat tiles (AGD code / provider /
 * method / location), and the accreditation + disclosure statements.
 * Purely presentational — extracted from LearnerCourseDetailLayout.
 */
export default function CourseCETab({ ceInfo, ceHours, hasContent }: CourseCETabProps) {
  if (!hasContent) {
    return (
      <Card sx={{ p: { xs: 2, md: 3 } }}>
        <EmptyTabMessage message="No CE information has been added for this course yet." />
      </Card>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* HERO — PACE seal in a soft cyan-tinted band, paired with
          a verified-CE headline. The accreditation TEXT moves
          into its own structured card below so this band stays
          visually decisive. */}
      <Card
        sx={{
          p: { xs: 2.5, md: 3.5 },
          background:
            'linear-gradient(135deg, rgba(var(--brand-rgb), 0.12) 0%, rgba(var(--brand-rgb), 0.04) 60%, rgba(255,255,255,1) 100%)',
          border: '1px solid',
          borderColor: 'rgba(var(--brand-rgb), 0.28)',
          // Read-only credential — kill the theme's default
          // hover shadow + transform so the tile reads as
          // static info, not a clickable card.
          transition: 'none',
          '&:hover': { boxShadow: 'none' },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'auto 1fr' },
            gap: { xs: 2.5, md: 4 },
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: 2,
              p: { xs: 1.5, md: 2 },
              boxShadow: '0 12px 32px -16px rgba(15,40,80,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              mx: { xs: 'auto', md: 0 },
            }}
          >
            <Box
              component="img"
              src={ceInfo.paceBadgeUrl || defaultPaceBadge}
              alt="PACE accreditation badge"
              sx={{
                height: { xs: 96, md: 120 },
                width: 'auto',
                maxWidth: 300,
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </Box>
          <Box>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.25,
                py: 0.5,
                borderRadius: 999,
                bgcolor: 'rgba(46,125,50,0.12)',
                color: 'success.main',
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                mb: 1.5,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 14 }} />
              AGD PACE Approved
            </Box>
            <Typography variant="h4" sx={{ mb: 0.75, lineHeight: 1.15 }}>
              Continuing Education Credit
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 540 }}>
              This course is accredited for continuing education. Track your hours
              and certifications below.
            </Typography>
          </Box>
        </Box>
      </Card>

      {/* AT A GLANCE — Stat tiles. Each fact gets its own card
          with an icon disc, value-first hierarchy, and a small
          label underneath. Auto-fit grid stacks gracefully. */}
      {(ceHours != null ||
        ceInfo.agdSubjectCode ||
        ceInfo.paceProviderId ||
        ceInfo.educationalMethod ||
        ceInfo.location) && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
            gap: 1.5,
          }}
        >
          {ceHours != null && (
            <CEStatCard
              icon={<PlayCircleIcon />}
              label="CE hours"
              value={String(ceHours)}
              tint="rgba(var(--brand-rgb), 0.18)"
              iconColor="rgb(var(--brand-rgb))"
            />
          )}
          {ceInfo.agdSubjectCode && (
            <CEStatCard
              icon={<LibraryBooksIcon />}
              label="AGD subject code"
              value={ceInfo.agdSubjectCode}
              tint="rgba(106,27,154,0.12)"
              iconColor="#6a1b9a"
            />
          )}
          {ceInfo.paceProviderId && (
            <CEStatCard
              icon={<CertificateIcon />}
              label="PACE Provider ID"
              value={ceInfo.paceProviderId}
              tint="rgba(46,125,50,0.12)"
              iconColor="#2e7d32"
            />
          )}
          {ceInfo.educationalMethod && (
            <CEStatCard
              icon={<DateRangeIcon />}
              label="Educational method"
              value={ceInfo.educationalMethod}
              tint="rgba(245,124,0,0.12)"
              iconColor="#e65100"
            />
          )}
          {ceInfo.location && (
            <CEStatCard
              icon={<HomeIcon />}
              label="Location"
              value={ceInfo.location}
              tint="rgba(21,101,192,0.12)"
              iconColor="#1565c0"
            />
          )}
        </Box>
      )}

      {/* STATEMENTS — Accreditation + Disclosure side by side
          when both exist; stacked otherwise. Each statement is
          a card with an icon header so the role of the block
          is obvious before the learner starts reading. */}
      {(ceInfo.accreditationStatement || ceInfo.disclosureStatement) && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md:
                ceInfo.accreditationStatement && ceInfo.disclosureStatement
                  ? '1fr 1fr'
                  : '1fr',
            },
            gap: 2,
          }}
        >
          {ceInfo.accreditationStatement && (
            <CEStatementCard
              icon={<InfoIcon />}
              label="Accreditation statement"
              body={ceInfo.accreditationStatement}
              tint="rgba(var(--brand-rgb), 0.10)"
              iconColor="rgb(var(--brand-rgb))"
              border="rgba(var(--brand-rgb), 0.28)"
            />
          )}
          {ceInfo.disclosureStatement && (
            <CEStatementCard
              icon={<WarningIcon />}
              label="Disclosure"
              body={ceInfo.disclosureStatement}
              tint="rgba(245,124,0,0.08)"
              iconColor="#e65100"
              border="rgba(245,124,0,0.25)"
            />
          )}
        </Box>
      )}
    </Box>
  );
}

/**
 * One stat tile on the CE dashboard. Compact card with a tinted icon
 * disc on the left, value-first hierarchy on the right, and a tiny
 * overline label below the value so the eye lands on the data first.
 */
function CEStatCard({
  icon,
  label,
  value,
  tint,
  iconColor,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tint: string;
  iconColor: string;
}) {
  return (
    <Card
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        height: '100%',
        // Static info tile — disable the theme's default Card hover so
        // the user doesn't get a clickable affordance for something
        // that isn't interactive.
        transition: 'none',
        '&:hover': { boxShadow: 'none' },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: tint,
          color: iconColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          '& svg': { fontSize: 20 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontWeight: 600,
            fontSize: '0.65rem',
            mb: 0.25,
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            lineHeight: 1.25,
            wordBreak: 'break-word',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Card>
  );
}

/**
 * Full-text statement card (accreditation / disclosure). Icon header
 * sets the role, then the body renders with relaxed line-height so
 * dense compliance copy reads comfortably.
 */
function CEStatementCard({
  icon,
  label,
  body,
  tint,
  iconColor,
  border,
}: {
  icon: React.ReactNode;
  label: string;
  body: string;
  tint: string;
  iconColor: string;
  border: string;
}) {
  return (
    <Card
      sx={{
        p: { xs: 2, md: 2.5 },
        border: '1px solid',
        borderColor: border,
        boxShadow: 'none',
        // Static read-only block — no hover affordance needed.
        transition: 'none',
        '&:hover': { boxShadow: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: tint,
            color: iconColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            '& svg': { fontSize: 18 },
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: iconColor,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            fontWeight: 700,
            fontSize: '0.7rem',
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          whiteSpace: 'pre-line',
          lineHeight: 1.7,
          color: 'text.primary',
        }}
      >
        {body}
      </Typography>
    </Card>
  );
}
